package com.havn.app.features.attachments.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnPickedFile
import com.havn.app.core.platform.havnCurrentWebHost
import com.havn.app.core.supabase.HavnSupabaseConfig
import com.havn.app.features.attachments.domain.AgentStorageUsage
import com.havn.app.features.attachments.domain.AttachmentRepository
import com.havn.app.features.attachments.domain.AttachmentUploadContext
import com.havn.app.features.attachments.domain.HAVN_ATTACHMENT_HARD_MAX_BYTES
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.features.attachments.domain.HavnAttachmentRecipient
import com.havn.app.features.attachments.domain.HavnAttachmentRecipientSource
import com.havn.app.features.attachments.domain.HavnAttachmentShareLinkTarget
import com.havn.app.features.attachments.domain.HavnAttachmentSharePhoto
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.storage.storage
import io.ktor.http.ContentType
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.minutes
import kotlin.time.ExperimentalTime
import kotlin.random.Random

class SupabaseAttachmentRepository(
    private val client: SupabaseClient
) : AttachmentRepository {
    override suspend fun allAttachments(): Result<List<HavnAttachment>> {
        return runCatching {
            client.from(ATTACHMENTS)
                .select {
                    order("updated_at", Order.DESCENDING)
                    order("created_at", Order.DESCENDING)
                }
                .decodeList<AttachmentRow>()
                .map { it.toDomain() }
        }.onFailure {
            if (!it.isMissingAttachmentSchema()) {
                HavnLogger.error("Attachments", "Failed to load all attachments", it)
            }
        }.recoverCatching { error ->
            if (error.isMissingAttachmentSchema()) emptyList() else throw error
        }
    }

    override suspend fun attachmentsForContext(propertyId: String?, taskId: String?): Result<List<HavnAttachment>> {
        return runCatching {
            val validPropertyId = propertyId?.takeIf { it.isNotBlank() }
            val validTaskId = taskId?.takeIf { it.isNotBlank() }
            require(validPropertyId != null || validTaskId != null) { "Attachment context is required." }
            client.from(ATTACHMENTS)
                .select {
                    filter {
                        validPropertyId?.let { eq("property_id", it) }
                        validTaskId?.let { eq("task_id", it) }
                    }
                    order("created_at", Order.DESCENDING)
                }
                .decodeList<AttachmentRow>()
                .map { it.toDomain() }
        }.onFailure {
            if (!it.isMissingAttachmentSchema()) {
                HavnLogger.error("Attachments", "Failed to load attachments", it)
            }
        }.recoverCatching { error ->
            if (error.isMissingAttachmentSchema()) emptyList() else throw error
        }
    }

    override suspend fun uploadAttachment(
        agentId: String,
        file: HavnPickedFile,
        context: AttachmentUploadContext
    ): Result<HavnAttachment> {
        return runCatching {
            require(context.propertyId != null || context.taskId != null || context.agencyId != null) { "Choose a property, task, or agency before attaching a file." }
            require(file.bytes.isNotEmpty()) { "Choose a non-empty file before uploading." }
            require(file.bytes.size.toLong() <= HAVN_ATTACHMENT_HARD_MAX_BYTES) { "File attachments are limited to 100 MB." }

            val usage = storageUsage().getOrNull()
            if (usage != null && usage.maxFileSizeBytes <= 0) {
                error("Documents are not included in your current plan. Upgrade Plan to upload files.")
            }
            if (usage != null && file.bytes.size.toLong() > usage.maxFileSizeBytes) {
                error("File size exceeds your current plan limit. Upgrade Plan to upload larger files.")
            }
            if (usage != null && usage.limitBytes <= 0) {
                error("Document storage is not included in your current plan. Upgrade Plan to upload files.")
            }
            if (usage != null && file.bytes.size.toLong() > usage.remainingBytes) {
                error("Storage limit reached for your current plan. Upgrade Plan to upload more files.")
            }

            val safeName = file.fileName.sanitizeFileName()
            val storagePath = buildString {
                append(agentId)
                append("/")
                context.propertyId?.let { append("properties/").append(it).append("/") }
                context.taskId?.let { append("tasks/").append(it).append("/") }
                append(timestampSlug())
                append("-")
                append(safeName)
            }
            val contentType = file.contentType.ifBlank { "application/octet-stream" }
            HavnLogger.info("Attachments", "Uploading attachment to $storagePath")
            try {
                client.storage.from(ATTACHMENTS_BUCKET).upload(storagePath, file.bytes) {
                    upsert = false
                    this.contentType = ContentType.parse(contentType)
                }
            } catch (error: Throwable) {
                if (error.isMissingAttachmentStorage()) {
                    throw IllegalStateException(
                        "Attachment storage is not enabled yet. Apply the Supabase attachments migration to create the attachments bucket.",
                        error
                    )
                }
                throw error
            }

            try {
                client.from(ATTACHMENTS)
                    .insert(
                        AttachmentInsertRow(
                            storagePath = storagePath,
                            fileName = safeName,
                            contentType = contentType,
                            sizeBytes = file.bytes.size.toLong(),
                            uploadedByAgentId = agentId,
                            agencyId = context.agencyId,
                            propertyId = context.propertyId,
                            taskId = context.taskId
                        )
                    ) {
                        select()
                    }
                    .decodeSingle<AttachmentRow>()
                    .toDomain()
            } catch (error: Throwable) {
                runCatching { client.storage.from(ATTACHMENTS_BUCKET).delete(storagePath) }
                throw error
            }
        }.onFailure {
            HavnLogger.error("Attachments", "Failed to upload attachment for $agentId", it)
        }
    }

    override suspend fun openUrlForAttachment(attachment: HavnAttachment): Result<String> {
        return runCatching {
            client.storage.from(ATTACHMENTS_BUCKET)
                .createSignedUrl(attachment.storagePath, 10.minutes)
        }.onFailure {
            HavnLogger.error("Attachments", "Failed to create attachment open URL ${attachment.id}", it)
        }
    }

    override suspend fun downloadAttachment(attachment: HavnAttachment): Result<ByteArray> {
        return runCatching {
            client.storage.from(ATTACHMENTS_BUCKET)
                .downloadAuthenticated(attachment.storagePath)
        }.onFailure {
            HavnLogger.error("Attachments", "Failed to download attachment ${attachment.id}", it)
        }
    }

    override suspend fun downloadSharePhoto(photo: HavnAttachmentSharePhoto): Result<ByteArray> {
        return runCatching {
            val bucket = requireNotNull(photo.bucketId) { "Photo storage bucket is missing." }
            val path = requireNotNull(photo.storagePath) { "Photo storage path is missing." }
            client.storage.from(bucket).downloadPublic(path)
        }.onFailure {
            HavnLogger.error("Attachments", "Failed to download share photo ${photo.id}", it)
        }
    }

    override suspend fun shareUrlsForAttachments(attachments: List<HavnAttachment>): Result<List<Pair<HavnAttachment, String>>> {
        return runCatching {
            attachments.map { attachment ->
                attachment to client.storage.from(ATTACHMENTS_BUCKET)
                    .createSignedUrl(attachment.storagePath, 7.days)
            }
        }.onFailure {
            HavnLogger.error("Attachments", "Failed to create attachment share URLs", it)
        }
    }

    override suspend fun shortShareUrlsForTargets(
        agentId: String,
        targets: List<HavnAttachmentShareLinkTarget>
    ): Result<List<Pair<HavnAttachmentShareLinkTarget, String>>> {
        return runCatching {
            if (targets.isEmpty()) return@runCatching emptyList()
            val expiresAt = (Clock.System.now() + 7.days).toString()
            val rows = targets.map { target ->
                AttachmentShareLinkInsertRow(
                    token = randomShareToken(),
                    agentId = agentId,
                    title = target.title.take(200).ifBlank { "HAVN file" },
                    targetUrl = target.url,
                    expiresAt = expiresAt
                )
            }
            client.from(ATTACHMENT_SHARE_LINKS).insert(rows)
            targets.zip(rows).map { (target, row) ->
                target to "${havnShareLinkBaseUrl()}/s/${row.token}"
            }
        }.onFailure {
            HavnLogger.error("Attachments", "Failed to create short attachment share URLs", it)
        }
    }

    override suspend fun recentShareRecipients(agentId: String): Result<List<HavnAttachmentRecipient>> {
        return runCatching {
            client.from(ATTACHMENT_SHARE_RECENTS)
                .select {
                    filter { eq("agent_id", agentId) }
                    order("last_sent_at", Order.DESCENDING)
                    limit(50)
                }
                .decodeList<AttachmentShareRecentRow>()
                .map { it.toDomain() }
        }.onFailure {
            if (!it.isMissingAttachmentSchema()) {
                HavnLogger.error("Attachments", "Failed to load attachment share recents", it)
            }
        }.recoverCatching { error ->
            if (error.isMissingAttachmentSchema()) emptyList() else throw error
        }
    }

    override suspend fun recordShareRecipient(agentId: String, recipient: HavnAttachmentRecipient): Result<Unit> {
        return runCatching {
            client.from(ATTACHMENT_SHARE_RECENTS)
                .upsert(
                    AttachmentShareRecentUpsertRow(
                        agentId = agentId,
                        recipientKey = recipient.id,
                        recipientSource = recipient.source.databaseValue,
                        displayName = recipient.displayName,
                        email = recipient.email,
                        phone = recipient.phone
                    )
                )
            Unit
        }.onFailure {
            if (!it.isMissingAttachmentSchema()) {
                HavnLogger.error("Attachments", "Failed to record attachment share recent", it)
            }
        }.recoverCatching { error ->
            if (error.isMissingAttachmentSchema()) Unit else throw error
        }
    }

    override suspend fun deleteAttachment(agentId: String, attachment: HavnAttachment): Result<Unit> {
        return runCatching {
            require(attachment.uploadedByAgentId == agentId) { "Only the file owner can delete this attachment." }
            client.storage.from(ATTACHMENTS_BUCKET).delete(attachment.storagePath)
            client.from(ATTACHMENTS).delete {
                filter { eq("id", attachment.id) }
            }
            Unit
        }.onFailure {
            HavnLogger.error("Attachments", "Failed to delete attachment ${attachment.id}", it)
        }
    }

    override suspend fun storageUsage(): Result<AgentStorageUsage> {
        return runCatching {
            client.postgrest.rpc("current_agent_storage_usage")
                .decodeSingle<StorageUsageRow>()
                .toDomain()
        }.onFailure {
            if (!it.isMissingAttachmentSchema()) {
                HavnLogger.error("Attachments", "Failed to load storage usage", it)
            }
        }.recoverCatching { error ->
            if (error.isMissingAttachmentSchema()) AgentStorageUsage() else throw error
        }
    }

    private companion object {
        const val ATTACHMENTS = "attachments"
        const val ATTACHMENT_SHARE_RECENTS = "attachment_share_recents"
        const val ATTACHMENT_SHARE_LINKS = "attachment_share_links"
        const val ATTACHMENTS_BUCKET = "attachments"
    }
}

private const val SHARE_TOKEN_LENGTH = 10
private const val SHARE_TOKEN_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789"

private fun randomShareToken(): String =
    buildString {
        repeat(SHARE_TOKEN_LENGTH) {
            append(SHARE_TOKEN_ALPHABET[Random.nextInt(SHARE_TOKEN_ALPHABET.length)])
        }
    }

private fun havnShareLinkBaseUrl(): String =
    when (havnCurrentWebHost()?.substringBefore(":")) {
        "app.havnre.app" -> "https://app.havnre.app"
        "sandboxedapp.havnre.app" -> "https://sandboxedapp.havnre.app"
        else -> if (HavnSupabaseConfig.environment == "production") {
            "https://app.havnre.app"
        } else {
            "https://sandboxedapp.havnre.app"
        }
    }

@Serializable
private data class AttachmentRow(
    val id: String,
    @SerialName("storage_path") val storagePath: String,
    @SerialName("file_name") val fileName: String,
    @SerialName("content_type") val contentType: String? = null,
    @SerialName("size_bytes") val sizeBytes: Long,
    @SerialName("uploaded_by_agent_id") val uploadedByAgentId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("task_id") val taskId: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null
) {
    fun toDomain(): HavnAttachment = HavnAttachment(
        id = id,
        storagePath = storagePath,
        fileName = fileName,
        contentType = contentType,
        sizeBytes = sizeBytes,
        uploadedByAgentId = uploadedByAgentId,
        agencyId = agencyId,
        propertyId = propertyId,
        taskId = taskId,
        createdAt = createdAt,
        updatedAt = updatedAt
    )
}

@Serializable
private data class AttachmentInsertRow(
    @SerialName("storage_path") val storagePath: String,
    @SerialName("file_name") val fileName: String,
    @SerialName("content_type") val contentType: String,
    @SerialName("size_bytes") val sizeBytes: Long,
    @SerialName("uploaded_by_agent_id") val uploadedByAgentId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("task_id") val taskId: String? = null
)

@Serializable
private data class StorageUsageRow(
    @SerialName("used_bytes") val usedBytes: Long = 0,
    @SerialName("limit_bytes") val limitBytes: Long = 0,
    @SerialName("remaining_bytes") val remainingBytes: Long = 0,
    @SerialName("max_file_size_bytes") val maxFileSizeBytes: Long = 0
) {
    fun toDomain(): AgentStorageUsage = AgentStorageUsage(
        usedBytes = usedBytes,
        limitBytes = limitBytes,
        remainingBytes = remainingBytes,
        maxFileSizeBytes = maxFileSizeBytes
    )
}

@Serializable
private data class AttachmentShareRecentRow(
    @SerialName("recipient_key") val recipientKey: String,
    @SerialName("recipient_source") val recipientSource: String,
    @SerialName("display_name") val displayName: String,
    val email: String? = null,
    val phone: String? = null,
    @SerialName("last_sent_at") val lastSentAt: String? = null
) {
    fun toDomain(): HavnAttachmentRecipient = HavnAttachmentRecipient(
        id = recipientKey,
        displayName = displayName,
        email = email,
        phone = phone,
        source = HavnAttachmentRecipientSource.fromDatabase(recipientSource),
        lastSentAt = lastSentAt
    )
}

@Serializable
private data class AttachmentShareRecentUpsertRow(
    @SerialName("agent_id") val agentId: String,
    @SerialName("recipient_key") val recipientKey: String,
    @SerialName("recipient_source") val recipientSource: String,
    @SerialName("display_name") val displayName: String,
    val email: String? = null,
    val phone: String? = null
)

@Serializable
private data class AttachmentShareLinkInsertRow(
    val token: String,
    @SerialName("agent_id") val agentId: String,
    val title: String,
    @SerialName("target_url") val targetUrl: String,
    @SerialName("expires_at") val expiresAt: String
)

private fun String.sanitizeFileName(): String =
    trim()
        .ifBlank { "attachment" }
        .replace(Regex("[^A-Za-z0-9._-]+"), "-")
        .trim('-')
        .take(140)
        .ifBlank { "attachment" }

@OptIn(ExperimentalTime::class)
private fun timestampSlug(): String =
    Clock.System.now().toString()
        .replace(":", "")
        .replace(".", "")
        .replace("-", "")
        .replace("T", "-")
        .replace("Z", "")

private fun Throwable.isMissingAttachmentSchema(): Boolean {
    val text = message.orEmpty()
    return text.contains("public.attachments", ignoreCase = true) ||
        text.contains("attachment_share_recents", ignoreCase = true) ||
        text.contains("current_agent_storage_usage", ignoreCase = true) ||
        text.contains("schema cache", ignoreCase = true)
}

private fun Throwable.isMissingAttachmentStorage(): Boolean {
    val text = message.orEmpty()
    return text.contains("bucket not found", ignoreCase = true) ||
        (text.contains("attachments", ignoreCase = true) && text.contains("bucket", ignoreCase = true))
}
